import React, { Component, ErrorInfo, ReactNode } from 'react';
import Button from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    // Here you could log the error to an error reporting service
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    // Force a reload of the page to clear any lingering issues
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">Algo deu errado</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Ocorreu um erro inesperado. Por favor, tente novamente ou entre em contato com o suporte.
            </p>
            {this.state.error && (
              <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md mb-6">
                <p className="text-sm text-red-800 dark:text-red-300 font-mono overflow-auto">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={this.handleReset}>Tentar Novamente</Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;